import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { Transition } from '@headlessui/react';
import { BellIcon, Bars3Icon as MenuIcon, XMarkIcon as XIcon } from '@heroicons/react/24/outline';
import GlobalNotification from './GlobalNotification';

function Layout() {
  const { user, logout } = useAuth();
  const { notifications } = useNotification();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  
  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Add defensive check to ensure notifications is an array before filtering
  const unreadNotifications = Array.isArray(notifications) ? notifications.filter(n => !n.read) : [];

  return (
    <div className="min-h-screen flex flex-col">
      <GlobalNotification />
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo and main nav */}
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/" className="flex items-center">
                  <span className="text-2xl font-bold text-primary-600">WASTE<span className="text-secondary-500">NOT</span></span>
                </Link>
              </div>
              
              {/* Desktop menu */}
              <nav className="hidden md:ml-6 md:flex md:space-x-8">
                <Link to="/" className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname === '/' ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>
                  Home
                </Link>
                <Link to="/listings" className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname.startsWith('/listings') ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>
                  Food Listings
                </Link>
                {user && (
                  <Link to="/dashboard" className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname.startsWith('/dashboard') ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>
                    Dashboard
                  </Link>
                )}
                {user?.role === 'admin' && (
                  <Link to="/admin" className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname.startsWith('/admin') ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>
                    Admin
                  </Link>
                )}
                {user?.role === 'recipient' && (
                  <Link to="/requests/manage" className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname.startsWith('/requests/manage') ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>
                    My Requests
                  </Link>
                )}
                {user?.role === 'donor' && (
                  <Link to="/requests/donor" className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${location.pathname.startsWith('/requests/donor') ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>
                    Donation Requests
                  </Link>
                )}
              </nav>
            </div>
            
            {/* Right side actions */}
            <div className="hidden md:ml-6 md:flex md:items-center">
              {user ? (
                <>
                  {/* Notifications */}
                  <div className="ml-3 relative">
                    <button 
                      onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                      className="p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <span className="sr-only">View notifications</span>
                      <BellIcon className="h-6 w-6" />
                      {unreadNotifications.length > 0 && (
                        <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white"></span>
                      )}
                    </button>
                    
                    {/* Notification dropdown */}
                    <Transition
                      show={isNotificationsOpen}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                      className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                    >
                      <div className="py-1">
                        <div className="px-4 py-2 border-b border-gray-200">
                          <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
                        </div>
                        
                        {/* Notification list */}
                        <div className="max-h-60 overflow-y-auto">
                          {Array.isArray(notifications) && notifications.length > 0 ? (
                            notifications.map(notification => (
                              <div key={notification.id} className={`px-4 py-2 hover:bg-gray-50 ${!notification.read ? 'bg-primary-50' : ''}`}>
                                <div className="flex justify-between items-start">
                                  <h4 className="text-sm font-medium text-gray-900">{notification.title}</h4>
                                  {notification.type && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                      {notification.type}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-700 mt-1">{notification.message}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {notification.createdAt ? notification.createdAt.toLocaleString() : new Date(notification.created_at).toLocaleString()}
                                </p>
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-sm text-gray-500">No notifications</div>
                          )}
                        </div>
                        
                        {/* View all link */}
                        {notifications?.length > 0 && (
                          <div className="border-t border-gray-200 px-4 py-2">
                            <Link to="/notifications" className="text-xs text-primary-600 hover:text-primary-800">
                              View all notifications
                            </Link>
                          </div>
                        )}
                      </div>
                    </Transition>
                  </div>
                  
                  {/* Profile dropdown */}
                  <div className="ml-3 relative">
                    <div className="flex items-center space-x-3">
                      <Link to="/profile" className="text-sm font-medium text-gray-700 hover:text-gray-800">
                        {user.name || user.email}
                      </Link>
                      <button 
                        onClick={logout}
                        className="text-sm font-medium text-gray-500 hover:text-gray-700"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link to="/login" className="text-sm font-medium text-gray-500 hover:text-gray-900">
                    Sign in
                  </Link>
                  <Link to="/register" className="btn-primary text-sm">
                    Register
                  </Link>
                </div>
              )}
            </div>
            
            {/* Mobile menu button */}
            <div className="flex items-center md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              >
                <span className="sr-only">{isMobileMenuOpen ? 'Close main menu' : 'Open main menu'}</span>
                {isMobileMenuOpen ? (
                  <XIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile menu, show/hide based on menu state. */}
        <div className={`md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
          <div className="pt-2 pb-3 space-y-1">
            <Link to="/" className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${location.pathname === '/' ? 'border-primary-500 text-primary-700 bg-primary-50' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'}`}>
              Home
            </Link>
            <Link to="/listings" className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${location.pathname.startsWith('/listings') ? 'border-primary-500 text-primary-700 bg-primary-50' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'}`}>
              Food Listings
            </Link>
            {user && (
              <Link to="/dashboard" className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${location.pathname.startsWith('/dashboard') ? 'border-primary-500 text-primary-700 bg-primary-50' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'}`}>
                Dashboard
              </Link>
            )}
            {user?.role === 'admin' && (
              <Link to="/admin" className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${location.pathname.startsWith('/admin') ? 'border-primary-500 text-primary-700 bg-primary-50' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'}`}>
                Admin
              </Link>
            )}
          </div>
          
          {/* Mobile authentication links */}
          <div className="pt-4 pb-3 border-t border-gray-200">
            {user ? (
              <>
                <div className="flex items-center px-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-500 font-medium">
                        {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-800">{user.name || user.email}</div>
                    <div className="text-sm font-medium text-gray-500">{user.email}</div>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  <Link to="/profile" className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100">
                    Profile
                  </Link>
                  {user?.role === 'recipient' && (
                    <Link to="/requests/manage" className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100">
                      My Requests
                    </Link>
                  )}
                  {user?.role === 'donor' && (
                    <Link to="/requests/donor" className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100">
                      Donation Requests
                    </Link>
                  )}
                  <Link to="/notifications" className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100">
                    Notifications
                    {unreadNotifications.length > 0 && (
                      <span className="ml-2 inline-block h-2 w-2 rounded-full bg-red-400"></span>
                    )}
                  </Link>
                  <button
                    onClick={logout}
                    className="block w-full text-left px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-3 space-y-1">
                <Link to="/login" className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100">
                  Sign in
                </Link>
                <Link to="/register" className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100">
                  Register
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>
      
      {/* Page content */}
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Outlet />
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="md:flex md:justify-between">
            <div className="mb-8 md:mb-0">
              <span className="text-xl font-bold text-primary-600">WASTE<span className="text-secondary-500">NOT</span></span>
              <p className="mt-2 text-sm text-gray-500">Reducing food waste, alleviating hunger in Kenya.</p>
            </div>
            <div className="grid grid-cols-2 gap-8 md:gap-16">
              <div>
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Resources</h3>
                <ul className="mt-4 space-y-2">
                  <li>
                    <Link to="/about" className="text-base text-gray-500 hover:text-gray-900">About</Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-base text-gray-500 hover:text-gray-900">FAQs</Link>
                  </li>
                  <li>
                    <Link to="/contact" className="text-base text-gray-500 hover:text-gray-900">Contact Us</Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Legal</h3>
                <ul className="mt-4 space-y-2">
                  <li>
                    <Link to="/privacy" className="text-base text-gray-500 hover:text-gray-900">Privacy Policy</Link>
                  </li>
                  <li>
                    <Link to="/terms" className="text-base text-gray-500 hover:text-gray-900">Terms of Service</Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-8 border-t border-gray-200 pt-8 md:flex md:items-center md:justify-between">
            <div className="text-sm text-gray-400">
              &copy; {new Date().getFullYear()} WASTENOT. All rights reserved.
            </div>
            <div className="mt-4 md:mt-0">
              {/* Social links would go here */}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
